import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersistence from 'vuex-persist';
import { idb } from './persistent-store';

import {
  LatLng,
  RootStore,
  Hospital,
  HospitalReport,
  SpecialCareValue,
  PatientDetails,
  PatientIntent,
  PatientState,
  CovidState,
  HospitalLocation,
  ConfidentValue,
  ReporterType,
  ConfidenceLevel,
  HospitalStatusDetails,
  ConfidenceValue,
  ItemStatus,
  Capacity,
  ReportAttribution,
} from './types';
import { Api } from './api';

Vue.use(Vuex);

const vuexIdb = new VuexPersistence({
  asyncStorage: true,
  storage: idb,
  reducer: (state: any) => {
    return {
      ...state,
      route: undefined,
    };
  },
  saveState: (key: any, state: any, storage: any) => {
    storage!.setItem(key, state);
  },
});

export enum StoreActions {
  SAVE_LOCATION = 'saveLocation',
  SAVE_CITY = 'saveCity',
  LOAD_HOSPITALS = 'loadHospitals',
  MAKE_REPORT = 'makeReport',
  SAVE_AGE = 'saveAge',
  SAVE_CARE_NEEDS = 'saveCareNeeds',
  SAVE_GENDER = 'saveGender',
  SAVE_STATE = 'saveState',
  SAVE_COVID_STATE = 'SAVE_COVID_STATE',
  UPDATE_PATIENT_NEEDS = 'UPDATE_PATIENT_NEEDS',
  FIND_MATCH = 'FIND_MATCH',
  EXPLORE_HOSPITALS = 'EXPLORE_HOSPITALS',
  SAVE_REPORTER = 'SAVE_REPORTER',
  SAVE_WAIT_TIME = 'SAVE_WAIT_TIME',
  SAVE_COVID_BEDS = 'SAVE_COVID_BEDS',
  SAVE_ICU_BEDS = 'SAVE_ICU_BEDS',
  SAVE_VENT = 'SAVE_VENT',
  SAVE_TOTAL_VENT = 'SAVE_TOTAL_VENT',
  SAVE_ADDITIONAL_COMMENTS = 'SAVE_ADDITIONAL_COMMENTS',
  DROP_REPORT = 'DROP_REPORT',
  SAVE_REPORTER_EMAIL = 'SAVE_REPORTER_EMAIL',
  SUBMIT_REPORTER_EMAIL = 'SUBMIT_REPORTER_EMAIL',
  SAVE_USER_NAME = 'SAVE_USER_NAME',
  LOAD_HOSPITAL_DETAILS = 'LOAD_HOSPITAL_DETAILS',
}
export enum StoreGetters {
  GET_LOCATION = 'getLocation',
  GET_CITY = 'getCity',
  GET_HOSPITALS = 'getHospitals',
  GET_AGE = 'getAge',
  GET_CARE_NEEDS = 'getCareNeeds',
  GET_GENDER = 'getGender',
  GET_STATE = 'getState',
  GET_COVID_STATE = 'GET_COVID_STATE',
  GET_PATIENT_NEEDS = 'GET_PATIENT_NEEDS',
  GET_REPORTER = 'GET_REPORTER',
  GET_WAIT_TIME = 'GET_WAIT_TIME',
  GET_COVID_BEDS = 'GET_COVID_BEDS',
  GET_ICU_BEDS = 'GET_ICU_BEDS',
  GET_VENT = 'GET_VENT',
  GET_TOTAL_VENT = 'GET_TOTAL_VENT',
  GET_ADDITIONAL_COMMENTS = 'GET_ADDITIONAL_COMMENTS',
  GET_REPORTER_EMAIL = 'GET_REPORTER_EMAIL',
  GET_USER_NAME = 'GET_USER_NAME',
  GET_HOSPITAL_DETAILS_BY_ID = 'GET_HOSPITAL_DETAILS_BY_ID',
}

enum StoreMutations {
  SAVE_LOCATION = 'saveLocation',
  SAVE_CITY = 'saveCity',
  SAVE_HOSPITALS = 'saveHospitals',
  SAVE_AGE = 'saveAge',
  SAVE_CARE_NEEDS = 'saveCareNeeds',
  SAVE_GENDER = 'saveGender',
  SAVE_STATE = 'saveState',
  SAVE_COVID_STATE = 'SAVE_COVID_STATE',
  UPDATE_PATIENT_NEEDS = 'UPDATE_PATIENT_NEEDS',
  SAVE_REPORTER = 'SAVE_REPORTER',
  SAVE_WAIT_TIME = 'SAVE_WAIT_TIME',
  SAVE_COVID_BEDS = 'SAVE_COVID_BEDS',
  SAVE_ICU_BEDS = 'SAVE_ICU_BEDS',
  SAVE_VENT = 'SAVE_VENT',
  SAVE_TOTAL_VENT = 'SAVE_TOTAL_VENT',
  SAVE_ADDITIONAL_COMMENTS = 'SAVE_ADDITIONAL_COMMENTS',
  DROP_REPORT = 'DROP_REPORT',
  SAVE_REPORTER_EMAIL = 'SAVE_REPORTER_EMAIL',
  SAVE_LAST_REPORT_ID = 'SAVE_LAST_REPORT_ID',
  SAVE_USER_NAME = 'SAVE_USER_NAME',
  SAVE_HOSPITAL_DETAILS = 'SAVE_HOSPITAL_DETAILS',
}

export async function collectUserDetails(
  state: RootStore,
  intent: PatientIntent,
): Promise<PatientDetails> {
  const patientState = ['NONE', 'MILD', 'SEVERE', 'CRITICAL'][state.state];
  const covidState = ['NONE', 'LIKELY', 'TESTED', 'CONFIRMED'][
    state.covidState
  ];
  const location: HospitalLocation = {
    lat: state.location!.lat,
    lng: state.location!.lng,
    city: state.city,
    address_line1: '',
    address_line2: '',
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    state_province: '',
    postal_code: '',
    country: '',
    county: '',
  };
  return {
    intent,
    patient_status: patientState as PatientState,
    covid19_status: covidState as CovidState,
    current_location: location,
    filed_at: Math.floor(new Date().valueOf() / 1000),
    immediate_medical_needs: [
      ...[
        state.needs.icu ? 'ICU' : '',
        state.needs.ventilator ? 'VENTILATOR' : '',
      ].filter(Boolean),
      ...state.careNeeds,
    ],
    patient_prognosis: [],
    patient_demographics: [state.gender],
    preferred_hospitals: [],
    preferred_regions: [],
    preferred_types: [],
  };
}

function fixConfidentValue(confidentValue: ConfidentValue): ConfidentValue {
  const { value, confidence } = confidentValue;
  return {
    value: +value + 0 === value || value === '' ? value : '',
    confidence: confidence + 0 === confidence ? confidence : 0,
  };
}

function getEmptyConfidentValue(): ConfidentValue {
  return {
    value: '',
    confidence: 0,
  };
}

const store = new Vuex.Store<RootStore>({
  state: {
    location: null,
    city: '',
    hospitals: [],
    age: '',
    gender: '',
    state: 0,
    covidState: 0,
    needs: {
      icu: false,
      ventilator: false,
    },
    reporter: -1,
    waitTime: {
      value: '',
      confidence: 0,
    },
    covidBedsReport: {
      value: '',
      confidence: 0,
    },
    icuBedsReport: {
      value: '',
      confidence: 0,
    },
    availableVentilatorsReport: {
      value: '',
      confidence: 0,
    },
    totalVentilatorsReport: {
      value: '',
      confidence: 0,
    },
    additionalCommentsReport: '',
    email: '',
    lastReportId: '',
    userName: '',
    hospitalsWithDetails: [],
    detailedHospitalsLastUpdate: {},
    careNeeds: [],
  },
  getters: {
    [StoreGetters.GET_CITY]: state => state.city,
    [StoreGetters.GET_LOCATION]: state => state.location,
    [StoreGetters.GET_HOSPITALS]: state => state.hospitals,
    [StoreGetters.GET_AGE]: state => state.age,
    [StoreGetters.GET_GENDER]: state => state.gender,
    [StoreGetters.GET_STATE]: state => state.state,
    [StoreGetters.GET_COVID_STATE]: state => state.covidState,
    [StoreGetters.GET_PATIENT_NEEDS]: state => state.needs,
    [StoreGetters.GET_REPORTER]: state => state.reporter,
    [StoreGetters.GET_WAIT_TIME]: state => fixConfidentValue(state.waitTime),
    [StoreGetters.GET_COVID_BEDS]: state =>
      fixConfidentValue(state.covidBedsReport),
    [StoreGetters.GET_ICU_BEDS]: state =>
      fixConfidentValue(state.icuBedsReport),
    [StoreGetters.GET_TOTAL_VENT]: state =>
      fixConfidentValue(state.totalVentilatorsReport),
    [StoreGetters.GET_VENT]: state =>
      fixConfidentValue(state.availableVentilatorsReport),
    [StoreGetters.GET_ADDITIONAL_COMMENTS]: state =>
      state.additionalCommentsReport,
    [StoreGetters.GET_REPORTER_EMAIL]: state => state.email,
    [StoreGetters.GET_USER_NAME]: state => state.userName,
    [StoreGetters.GET_HOSPITAL_DETAILS_BY_ID]: state => (hospitalId: string) =>
      state.hospitalsWithDetails.find(x => x.hospital.id === hospitalId),
    [StoreGetters.GET_CARE_NEEDS]: state => state.careNeeds,
  },
  mutations: {
    [StoreMutations.SAVE_CITY]: (state, city: string) => (state.city = city),
    [StoreMutations.SAVE_LOCATION]: (state, loc: LatLng) =>
      (state.location = loc),
    [StoreMutations.SAVE_HOSPITALS]: (state, hospitals: Hospital[]) =>
      (state.hospitals = hospitals),
    [StoreMutations.SAVE_AGE]: (state, age: string) => (state.age = age),
    [StoreMutations.SAVE_GENDER]: (state, gender: string) =>
      (state.gender = gender),
    [StoreMutations.SAVE_STATE]: (state, patState: number) =>
      (state.state = patState),
    [StoreMutations.SAVE_COVID_STATE]: (state, patState: number) =>
      (state.covidState = patState),
    [StoreMutations.SAVE_REPORTER]: (state, reporter: number) =>
      (state.reporter = reporter),
    [StoreMutations.UPDATE_PATIENT_NEEDS]: (state, needs: SpecialCareValue) =>
      (state.needs = needs),
    [StoreMutations.SAVE_WAIT_TIME]: (state, value: ConfidentValue) =>
      (state.waitTime = value),
    [StoreMutations.SAVE_COVID_BEDS]: (state, value: ConfidentValue) =>
      (state.covidBedsReport = value),
    [StoreMutations.SAVE_ICU_BEDS]: (state, value: ConfidentValue) =>
      (state.icuBedsReport = value),
    [StoreMutations.SAVE_TOTAL_VENT]: (state, value: ConfidentValue) =>
      (state.totalVentilatorsReport = value),
    [StoreMutations.SAVE_VENT]: (state, value: ConfidentValue) =>
      (state.availableVentilatorsReport = value),
    [StoreMutations.SAVE_ADDITIONAL_COMMENTS]: (state, value: string) =>
      (state.additionalCommentsReport = value),
    [StoreMutations.SAVE_REPORTER_EMAIL]: (state, value: string) =>
      (state.email = value),
    [StoreMutations.SAVE_LAST_REPORT_ID]: (state, value: string) =>
      (state.lastReportId = value),
    [StoreMutations.SAVE_USER_NAME]: (state, value: string) =>
      (state.userName = value),
    [StoreMutations.SAVE_HOSPITAL_DETAILS]: (
      state,
      value: HospitalStatusDetails,
    ) => {
      const hospitalIndex = state.hospitalsWithDetails.findIndex(
        x => x.hospital.id === value.hospital.id,
      );

      state.detailedHospitalsLastUpdate[
        value.hospital.id
      ] = new Date().valueOf();

      if (hospitalIndex >= 0) {
        state.hospitalsWithDetails.splice(hospitalIndex, 1, value);
      } else {
        state.hospitalsWithDetails.push(value);
      }
    },
    [StoreMutations.DROP_REPORT]: state => {
      state.waitTime = getEmptyConfidentValue();
      state.covidBedsReport = getEmptyConfidentValue();
      state.icuBedsReport = getEmptyConfidentValue();
      state.availableVentilatorsReport = getEmptyConfidentValue();
      state.totalVentilatorsReport = getEmptyConfidentValue();
      state.additionalCommentsReport = '';
      state.reporter = -1;
    },
    [StoreMutations.SAVE_CARE_NEEDS]: (state, value: string[]) =>
      (state.careNeeds = value),
  },
  actions: {
    [StoreActions.FIND_MATCH]: async ({ commit, state }) => {
      const request = await collectUserDetails(state, PatientIntent.NEEDHELP);
      const hospitals = await Api.loadMatches(request);
      commit(StoreMutations.SAVE_HOSPITALS, hospitals);
    },
    [StoreActions.EXPLORE_HOSPITALS]: async ({ commit, state }) => {
      const request = await collectUserDetails(
        state,
        PatientIntent.EXPLORATION,
      );
      const hospitals = await Api.loadMatches(request);
      commit(StoreMutations.SAVE_HOSPITALS, hospitals);
    },
    [StoreActions.LOAD_HOSPITAL_DETAILS]: async (
      { commit, state },
      hospitalId: string,
    ) => {
      const lastDataUpdate = state.detailedHospitalsLastUpdate[hospitalId];
      const LESS_THAN_3_MINS = 3 * 60 * 1000;
      if (
        lastDataUpdate &&
        Math.abs(lastDataUpdate - new Date().valueOf()) < LESS_THAN_3_MINS
      ) {
        return;
      }

      const response = await Api.loadHospital(hospitalId);
      commit(StoreMutations.SAVE_HOSPITAL_DETAILS, response);
    },
    [StoreActions.SAVE_CITY]: ({ commit }, city: string) =>
      commit(StoreMutations.SAVE_CITY, city),
    [StoreActions.SAVE_ADDITIONAL_COMMENTS]: ({ commit }, value: string) =>
      commit(StoreMutations.SAVE_ADDITIONAL_COMMENTS, value),
    [StoreActions.SAVE_REPORTER_EMAIL]: ({ commit }, value: string) =>
      commit(StoreMutations.SAVE_REPORTER_EMAIL, value),
    [StoreActions.SAVE_AGE]: ({ commit }, age: string) =>
      commit(StoreMutations.SAVE_AGE, age),
    [StoreActions.SAVE_GENDER]: ({ commit }, gender: string) =>
      commit(StoreMutations.SAVE_GENDER, gender),
    [StoreActions.SAVE_LOCATION]: ({ commit }, loc: LatLng) =>
      commit(StoreMutations.SAVE_LOCATION, loc),
    [StoreActions.SAVE_STATE]: ({ commit }, state: number) =>
      commit(StoreMutations.SAVE_STATE, state),
    [StoreActions.SAVE_USER_NAME]: ({ commit }, value: string) =>
      commit(StoreMutations.SAVE_USER_NAME, value),
    [StoreActions.SAVE_COVID_STATE]: ({ commit }, state: number) =>
      commit(StoreMutations.SAVE_COVID_STATE, state),
    [StoreActions.SAVE_REPORTER]: ({ commit }, reporter: number) =>
      commit(StoreMutations.SAVE_REPORTER, reporter),
    [StoreActions.SAVE_WAIT_TIME]: ({ commit }, value: ConfidentValue) =>
      commit(StoreMutations.SAVE_WAIT_TIME, value),
    [StoreActions.SAVE_COVID_BEDS]: ({ commit }, value: ConfidentValue) =>
      commit(StoreMutations.SAVE_COVID_BEDS, value),
    [StoreActions.SAVE_ICU_BEDS]: ({ commit }, value: ConfidentValue) =>
      commit(StoreMutations.SAVE_ICU_BEDS, value),
    [StoreActions.SAVE_TOTAL_VENT]: ({ commit }, value: ConfidentValue) =>
      commit(StoreMutations.SAVE_TOTAL_VENT, value),
    [StoreActions.SAVE_VENT]: ({ commit }, value: ConfidentValue) =>
      commit(StoreMutations.SAVE_VENT, value),
    [StoreActions.DROP_REPORT]: ({ commit }) =>
      commit(StoreMutations.DROP_REPORT),
    [StoreActions.UPDATE_PATIENT_NEEDS]: (
      { commit },
      needs: SpecialCareValue,
    ) => commit(StoreMutations.UPDATE_PATIENT_NEEDS, needs),
    [StoreActions.LOAD_HOSPITALS]: async ({ commit }) => {
      const hospitals = await Api.loadHospitals();
      commit(StoreMutations.SAVE_HOSPITALS, hospitals);
    },
    [StoreActions.SUBMIT_REPORTER_EMAIL]: async ({ state }) => {
      const reporter: ReporterType = [
        ReporterType.UNSPECIFIED,
        ReporterType.PATIENT,
        ReporterType.MEDICAL_WORKER,
        ReporterType.STAFF,
        ReporterType.OFFICIAL,
      ][state.reporter + 1];

      const attribution: ReportAttribution = {
        report_id: state.lastReportId,
        email: state.email,
        reporter_type: reporter,
      };

      await Api.attributeReport(attribution);
    },
    [StoreActions.MAKE_REPORT]: async (
      { state, commit },
      hospitalId: string,
    ) => {
      const getConfidence = (confidence: number): ConfidenceLevel => {
        return [
          ConfidenceLevel.GUESS,
          ConfidenceLevel.EDUCATED_ESTIMATION,
          ConfidenceLevel.CERTAIN,
        ][confidence];
      };

      const toConfidenceValue = (
        report?: ConfidentValue,
        f?: (n: number) => number,
      ): ConfidenceValue | undefined => {
        if (report && report.value !== '') {
          let value = report.value;
          if (f) {
            value = f(value);
          }
          return { value, confidence: getConfidence(report.confidence) };
        }
      };
      const toAvailable = (report?: ConfidentValue): Capacity | undefined => {
        if (toConfidenceValue(report)) {
          return { available: toConfidenceValue(report) };
        }
      };
      const report: HospitalReport = {
        hospital_id: hospitalId,
        corona_beds: toAvailable(state.covidBedsReport),
        icu_beds: toAvailable(state.icuBedsReport),
        adult_ventilators: {
          available: toConfidenceValue(state.availableVentilatorsReport),
          total: toConfidenceValue(state.totalVentilatorsReport),
        },
        description: state.additionalCommentsReport,
        current_wait_time_seconds: toConfidenceValue(
          state.waitTime,
          x => x * 60,
        ),
      };

      const resp = await Api.submitReport(report);
      commit(StoreMutations.SAVE_LAST_REPORT_ID, resp.report_id);
    },
    [StoreActions.SAVE_CARE_NEEDS]: ({ commit }, value: string[]) =>
      commit(StoreMutations.SAVE_CARE_NEEDS, value),
  },
  modules: {},
  plugins: [vuexIdb.plugin as any],
});

export function useStore() {
  return store;
}

export function saveLocation(loc: LatLng | null): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_LOCATION}`, loc);
}
export function getLocation(): LatLng | null {
  return store.getters[StoreGetters.GET_LOCATION];
}
export function saveCity(city: string): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_CITY}`, city);
}
export function getCity(): string {
  return store.getters[StoreGetters.GET_CITY];
}
export function saveAge(age: string): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_AGE}`, age);
}
export function getAge(): string {
  return store.getters[StoreGetters.GET_AGE];
}
export function saveAdditionalComments(value: string): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_ADDITIONAL_COMMENTS}`, value);
}
export function getAdditionalComments(): string {
  return store.getters[StoreGetters.GET_ADDITIONAL_COMMENTS];
}
export function saveGender(gender: string): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_GENDER}`, gender);
}
export function getGender(): string {
  return store.getters[StoreGetters.GET_GENDER];
}
export function saveState(state: number): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_STATE}`, state);
}
export function getState(): number {
  return store.getters[StoreGetters.GET_STATE];
}
export function saveSpecialNeeds(needs: SpecialCareValue): Promise<void> {
  return store.dispatch(`${StoreActions.UPDATE_PATIENT_NEEDS}`, needs);
}
export function getSpecialNeeds(): SpecialCareValue {
  return store.getters[StoreGetters.GET_PATIENT_NEEDS];
}
export function saveCovidState(state: number): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_COVID_STATE}`, state);
}
export function getCovidState(): number {
  return store.getters[StoreGetters.GET_COVID_STATE];
}
export function saveReporterEmail(value: string): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_REPORTER_EMAIL}`, value);
}
export function getReporterEmail(): string {
  return store.getters[StoreGetters.GET_REPORTER_EMAIL];
}
export function saveReporter(reporter: number): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_REPORTER}`, reporter);
}
export function getReporter(): number {
  return store.getters[StoreGetters.GET_REPORTER];
}
export function saveUserName(value: string): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_USER_NAME}`, value);
}
export function getUserName(): string {
  return store.getters[StoreGetters.GET_USER_NAME];
}
export function saveWaitTime(value: ConfidentValue): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_WAIT_TIME}`, value);
}
export function getWaitTime(): ConfidentValue {
  return store.getters[StoreGetters.GET_WAIT_TIME];
}
export function saveCovidBedsReport(value: ConfidentValue): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_COVID_BEDS}`, value);
}
export function getCovidBedsReport(): ConfidentValue {
  return store.getters[StoreGetters.GET_COVID_BEDS];
}
export function saveICUBedsReport(value: ConfidentValue): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_ICU_BEDS}`, value);
}
export function getICUBedsReport(): ConfidentValue {
  return store.getters[StoreGetters.GET_ICU_BEDS];
}
export function saveAvailableVentilatorsReport(
  value: ConfidentValue,
): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_VENT}`, value);
}
export function getAvailableVentilatorsReport(): ConfidentValue {
  return store.getters[StoreGetters.GET_VENT];
}
export function saveTotalVentilatorsReport(
  value: ConfidentValue,
): Promise<void> {
  return store.dispatch(`${StoreActions.SAVE_TOTAL_VENT}`, value);
}
export function getTotalVentilatorsReport(): ConfidentValue {
  return store.getters[StoreGetters.GET_TOTAL_VENT];
}
export function getHospitalsFromBackend(): Promise<void> {
  return store.dispatch(`${StoreActions.LOAD_HOSPITALS}`);
}
export function getHospitals(): Hospital[] {
  return store.getters[StoreGetters.GET_HOSPITALS];
}

export function getHospital(id: string): Hospital | undefined {
  return getHospitals().find(hosp => hosp.id == id);
}
export function getHospitalDetails(
  id: string,
): HospitalStatusDetails | undefined {
  return store.getters[`${StoreGetters.GET_HOSPITAL_DETAILS_BY_ID}`](id);
}
export function findHospitalMatch(): Promise<void> {
  return store.dispatch(`${StoreActions.FIND_MATCH}`);
}
export function exploreHospitals(): Promise<void> {
  return store.dispatch(`${StoreActions.EXPLORE_HOSPITALS}`);
}
export function submitReport(hospitalId: string): Promise<void> {
  return store.dispatch(`${StoreActions.MAKE_REPORT}`, hospitalId);
}
export function submitEmail(hospitalId: string): Promise<void> {
  return store.dispatch(`${StoreActions.SUBMIT_REPORTER_EMAIL}`, hospitalId);
}
export function dropReport(): Promise<void> {
  return store.dispatch(`${StoreActions.DROP_REPORT}`);
}
export function loadHospitalDetails(hospitalId: string): Promise<void> {
  return store.dispatch(`${StoreActions.LOAD_HOSPITAL_DETAILS}`, hospitalId);
}
export function saveCareNeeds(value: string[]): Promise<void> {
  return store.dispatch(StoreActions.SAVE_CARE_NEEDS, value);
}
export function getCareNeeds(): string[] {
  return store.getters[StoreGetters.GET_CARE_NEEDS];
}

export function getRouterParams() {
  // @ts-ignore
  return store.state['route']?.params || {};
}

export default store;
